import { useQuery } from '@tanstack/react-query';
import { AppWindow, Hammer, Laptop, LifeBuoy, Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AssetStatusBadge } from '../components/badges';
import { ModelImage } from '../components/model-image';
import { PendingSurveysAlert } from '../components/ticket-tracking';
import { WorkOrderStatusBadge } from '../components/work-order-badges';
import { WorkOrderFormModal } from '../components/WorkOrderFormModal';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, PageHeader, PageLoader, Table, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useDesk } from '../lib/desk';
import { equipmentLabel, placeLabel } from '../lib/facilities';
import { formatRelative } from '../lib/format';
import type { MyDevices, Page, ReportableAsset, WorkOrder } from '../lib/types';
import { REPORT_REASON_LABEL } from '../lib/work-orders';

/** A requester's own tickets: technology problems at /requests, facilities requests at /facilities/requests. */
export function MyRequestsPage() {
  const desk = useDesk();
  return desk.facilities ? <FacilitiesRequests /> : <TechnologyRequests />;
}

function TechnologyRequests() {
  const { me } = useAuth();
  // null: form closed. { device: null }: choose the device in the form.
  const [reporting, setReporting] = useState<{ device: ReportableAsset | null } | null>(null);
  const devices = useQuery({ queryKey: ['my-devices'], queryFn: () => api.get<MyDevices>('/my/devices') });
  const tickets = useQuery({
    queryKey: ['work-orders', 'mine', 'IT'],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', { mine: true, state: 'all', pageSize: 50 }),
  });

  const rooms = devices.data?.rooms ?? [];

  return (
    <>
      <PageHeader
        title="My requests"
        description="Report a problem with a device or software and follow what happens next."
        actions={
          <Button variant="primary" icon={<Wrench />} onClick={() => setReporting({ device: null })}>
            Report a problem
          </Button>
        }
      />

      <PendingSurveysAlert className="mb-6" />

      {devices.isLoading ? (
        <PageLoader />
      ) : devices.error ? (
        <ErrorState error={devices.error} onRetry={() => devices.refetch()} />
      ) : !devices.data!.person ? (
        <Alert tone="amber" title="We couldn’t find your devices">
          Your sign-in email ({me?.user.email}) doesn’t match an active person at {me?.tenant?.name}. Ask the technology office to check it.
          You can still report a problem using the asset tag on the device’s label.
        </Alert>
      ) : (
        <Card>
          <CardHeader
            title="Your devices"
            description={
              rooms.length
                ? `Devices assigned to you, and devices assigned to or kept in ${rooms.map((r) => r.name).join(', ')}`
                : 'Devices assigned to you'
            }
          />
          {devices.data!.assets.length === 0 ? (
            <EmptyState
              icon={<Laptop />}
              title="No devices to show"
              description="Nothing is checked out to you or your rooms. You can still report a problem using a device’s asset tag."
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {devices.data!.assets.map((asset) => (
                <li key={asset.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <ModelImage url={asset.imageUrl} alt={asset.model ?? ''} className="size-16" />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
                      {asset.name}
                      {asset.status !== 'CHECKED_OUT' && asset.status !== 'AVAILABLE' && <AssetStatusBadge status={asset.status} />}
                    </p>
                    <p className="text-xs text-slate-500">
                      <span className="font-mono">{asset.assetTag}</span>
                      {asset.model && ` · ${asset.model}`}
                      {asset.serialNumber && ` · Serial ${asset.serialNumber}`}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="brand">
                        {REPORT_REASON_LABEL[asset.reason]}
                        {asset.roomName && ` · ${asset.roomName}`}
                      </Badge>
                      {asset.workOrders.map((ticket) => (
                        <Link key={ticket.id} to={`/work-orders/${ticket.id}`} className="inline-flex items-center gap-1 text-brand-700 hover:underline">
                          #{ticket.number} {ticket.title}
                          <WorkOrderStatusBadge status={ticket.status} />
                        </Link>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" icon={<Wrench />} onClick={() => setReporting({ device: asset })}>
                    Report a problem
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader title="Your tickets" description="Problems you reported, or that were reported for you" />
        {tickets.isLoading ? (
          <PageLoader />
        ) : tickets.error ? (
          <ErrorState error={tickets.error} onRetry={() => tickets.refetch()} />
        ) : tickets.data!.items.length === 0 ? (
          <EmptyState icon={<LifeBuoy />} title="No tickets yet" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Ticket</Th>
                <Th>About</Th>
                <Th>Status</Th>
                <Th>Assigned to</Th>
                <Th>Last update</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.data!.items.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-slate-50/60">
                  <Td>
                    <Link to={`/work-orders/${ticket.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                    </Link>
                  </Td>
                  <Td className="text-slate-600">
                    {ticket.asset ? (
                      <>
                        <span className="font-mono text-xs">{ticket.asset.assetTag}</span> {ticket.asset.name}
                      </>
                    ) : ticket.software ? (
                      <span className="inline-flex items-center gap-1.5">
                        <AppWindow className="size-3.5 shrink-0 text-slate-400" />
                        {ticket.software.name}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </Td>
                  <Td>
                    <WorkOrderStatusBadge status={ticket.status} />
                  </Td>
                  <Td className="text-slate-600">{ticket.assignedTo?.name ?? <span className="text-slate-400">Not yet</span>}</Td>
                  <Td className="whitespace-nowrap text-slate-500">{formatRelative(ticket.updatedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <WorkOrderFormModal open={!!reporting} onClose={() => setReporting(null)} device={reporting?.device ?? null} />
    </>
  );
}

function FacilitiesRequests() {
  const desk = useDesk();
  const navigate = useNavigate();
  const [requesting, setRequesting] = useState(false);
  const tickets = useQuery({
    queryKey: ['work-orders', 'mine', desk.service],
    queryFn: () => api.get<Page<WorkOrder>>('/work-orders', desk.query({ mine: true, state: 'all', pageSize: 50 })),
    // Requesters see progress without reloading.
    refetchInterval: 60_000,
  });

  return (
    <>
      <PageHeader
        title="My facilities requests"
        description="Ask for repairs, cleaning, grounds work, or anything else the facilities team handles, and follow what happens next."
        actions={
          <Button variant="primary" icon={<Hammer />} onClick={() => setRequesting(true)}>
            Request work
          </Button>
        }
      />

      <PendingSurveysAlert className="mb-6" />

      <Card>
        <CardHeader title="Your requests" description="Requests you sent, or that the facilities team logged for you" />
        {tickets.isLoading ? (
          <PageLoader />
        ) : tickets.error ? (
          <ErrorState error={tickets.error} onRetry={() => tickets.refetch()} />
        ) : tickets.data!.items.length === 0 ? (
          <EmptyState
            icon={<LifeBuoy />}
            title="No requests yet"
            description="When something needs fixing, cleaning, or checking, send a request and the facilities team will pick it up."
            action={
              <Button variant="primary" icon={<Hammer />} onClick={() => setRequesting(true)}>
                Request work
              </Button>
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Request</Th>
                <Th>Where</Th>
                <Th>Status</Th>
                <Th>Assigned to</Th>
                <Th>Last update</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {tickets.data!.items.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-slate-50/60">
                  <Td className="max-w-xs">
                    <Link to={desk.workOrderHref(ticket.id)} className="block truncate font-medium text-slate-900 hover:text-brand-700">
                      <span className="text-slate-400">#{ticket.number}</span> {ticket.title}
                    </Link>
                  </Td>
                  <Td className="max-w-[16rem] text-slate-600">
                    <p className="truncate">
                      {placeLabel({ building: ticket.building ?? ticket.room?.building, room: ticket.room, locationDetail: ticket.locationDetail }) || '—'}
                    </p>
                    {ticket.facilityAsset && <p className="truncate text-xs text-slate-500">{equipmentLabel(ticket.facilityAsset)}</p>}
                  </Td>
                  <Td>
                    <WorkOrderStatusBadge status={ticket.status} />
                  </Td>
                  <Td className="text-slate-600">{ticket.assignedTo?.name ?? <span className="text-slate-400">Not yet</span>}</Td>
                  <Td className="whitespace-nowrap text-slate-500">{formatRelative(ticket.updatedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <WorkOrderFormModal open={requesting} onClose={() => setRequesting(false)} onCreated={(ticket) => navigate(desk.workOrderHref(ticket.id))} />
    </>
  );
}
